/**
 * La spedizione vera e propria: i file vanno all'archivio, non al servizio.
 *
 * **Mimo il servizio.** Il pacchetto e' fatto di decine di gigabyte; un
 * servizio sulla strada di ogni byte diventa il collo di bottiglia esattamente
 * quando si lavora. I collegamenti firmati esistono per questo, e qui si usano.
 *
 * **Pochi invii per volta.** Novanta PUT in parallelo non accorciano niente:
 * riempiono il canale, fanno scadere le firme di quelli in coda e rendono
 * l'avanzamento illeggibile — tutto al 3 % per un'ora.
 *
 * **Il guasto di un file non ferma la sessione.** Resta fra i mancanti, e il
 * giro dopo lo riprende. Rilanciare tutto per un file andato storto e' il modo
 * piu' rapido di trasformare due ore di lavoro in quattro.
 *
 * **Cio' che e' arrivato lo dice l'archivio**, non questa pagina: la ripresa e'
 * il confronto fra l'inventario e cio' che c'e' davvero (`GET /uploads/{id}`),
 * e una contabilita' tenuta qui direbbe «mandato» anche per un invio che il
 * navigatore ha buttato via chiudendo la scheda.
 */

/** Un oggetto da depositare, col collegamento firmato che lo accoglie. */
export interface Piece {
  path: string
  url: string
}

/** Quanto e' andato, mentre va. */
export interface Progress {
  done: number
  total: number
  bytesDone: number
  bytesTotal: number
  failed: string[]
}

/** Il deposito di un singolo oggetto. Iniettabile: le prove non hanno un
 *  archivio, e XMLHttpRequest non esiste in una prova senza navigatore. */
export type Put = (url: string, body: File, moved: (bytes: number) => void) => Promise<void>

/**
 * Quanti invii insieme.
 *
 * Quattro non e' un numero magico: e' abbastanza da coprire la latenza di rete
 * fra un file e l'altro, e poco abbastanza da non far scadere le firme di
 * quelli che aspettano.
 */
export const AT_ONCE = 4

/** Il deposito di un oggetto, con l'avanzamento in byte. */
export function put(url: string, body: File, moved: (bytes: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    // XMLHttpRequest e non `fetch`: `fetch` non dice quanti byte sono partiti,
    // e senza quel numero l'avanzamento avanza a scatti da un file all'altro —
    // su un file da 40 MB significa fermo per un minuto.
    const request = new XMLHttpRequest()
    request.open('PUT', url)
    request.upload.onprogress = (event) => moved(event.loaded)
    request.onload = () =>
      request.status >= 200 && request.status < 300
        ? resolve()
        : reject(new Error(`l'archivio ha risposto ${request.status}`))
    request.onerror = () => reject(new Error('la rete si e’ interrotta'))
    request.onabort = () => reject(new Error('invio interrotto'))
    request.send(body)
  })
}

/**
 * Spedisce cio' che manca e racconta quanto e' andato.
 *
 * Restituisce i percorsi che non sono arrivati. Non solleva: un guasto di rete
 * a meta' pacchetto e' un caso normale, e chi carica deve poter riprovare senza
 * ricominciare.
 */
export async function ship(
  pieces: readonly Piece[],
  blobs: ReadonlyMap<string, File>,
  watch: (progress: Progress) => void,
  send: Put = put,
  atOnce: number = AT_ONCE,
): Promise<string[]> {
  const failed: string[] = []
  const total = pieces.length
  const bytesTotal = pieces.reduce((sum, one) => sum + (blobs.get(one.path)?.size ?? 0), 0)
  // I byte in volo si contano a parte da quelli finiti: un invio che fallisce a
  // meta' deve *togliere* cio' che aveva mostrato, altrimenti l'avanzamento
  // supera il totale e chi guarda smette di crederci.
  const flying = new Map<string, number>()
  let bytesDone = 0
  let done = 0

  const say = (): void =>
    watch({
      done,
      total,
      bytesDone: bytesDone + [...flying.values()].reduce((sum, one) => sum + one, 0),
      bytesTotal,
      failed: [...failed],
    })

  let next = 0
  async function worker(): Promise<void> {
    for (;;) {
      const piece = pieces[next++]
      if (piece === undefined) return
      const body = blobs.get(piece.path)
      if (body === undefined) {
        // Il file non e' fra quelli scelti: succede se si riprende una sessione
        // scegliendo una cartella diversa. Si dice, non si finge.
        failed.push(piece.path)
        done += 1
        say()
        continue
      }
      try {
        await send(piece.url, body, (bytes) => {
          flying.set(piece.path, bytes)
          say()
        })
        bytesDone += body.size
      } catch {
        failed.push(piece.path)
      } finally {
        flying.delete(piece.path)
        done += 1
        say()
      }
    }
  }

  say()
  await Promise.all(Array.from({ length: Math.min(atOnce, pieces.length) }, () => worker()))
  return failed
}
